import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureDir, ensureFile } from '@/OS/utils/filesystem';
import { useUserVFS } from '@/OS/hooks/useUserVFS';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { MapleMarketApp } from '@/data/mapleMarketApps';
import {
  extractMapleMarketPackageArchive,
  sanitizeModuleName,
  type MapleMarketModuleManifest,
  type MapleMarketModuleType,
} from '@/OS/utils/mapleMarketPackages';
import {
  markPlayedMapleMarketProduct,
  saveInstalledMapleMarketProduct,
} from '@/lib/mapleMarketLibrary';

const PROGRAM_FILES_ROOT = 'C:\\Program Files';
const MODULES_ROOT = 'C:\\Program Files\\Modules';

type InstalledRuntimeAppId = 'web-app-runner' | 'module-host';

function resolveRuntimeAppId(type?: MapleMarketModuleType): InstalledRuntimeAppId {
  return type === 'wasm' ? 'module-host' : 'web-app-runner';
}

function createBundleManifest(product: MapleMarketApp, version: string): MapleMarketModuleManifest {
  return {
    name: product.name,
    version,
    description: product.description,
    author: product.developer,
    categories: Array.from(new Set([product.category, ...product.tags.slice(0, 3)])),
    permissions: ['notifications:show'],
    type: 'web',
    loader: 'bundle',
    entry: { html: 'index.html' },
    assets: 'vfs://app/',
    marketplace: {
      sku: product.sku,
      packageType: product.packageType,
      installedAt: new Date().toISOString(),
    },
  };
}

function readInstalledManifestContent(rootPath: string, getFile: (path: string) => { content: string | null } | null) {
  return getFile(`${rootPath}\\manifest.json`)?.content || null;
}

function resolveInstalledRuntimeFromManifestContent(manifestContent: string | null) {
  if (!manifestContent) {
    return resolveRuntimeAppId('web');
  }

  try {
    const manifest = JSON.parse(manifestContent) as MapleMarketModuleManifest;
    return resolveRuntimeAppId(manifest.type);
  } catch {
    return resolveRuntimeAppId('web');
  }
}

async function ensureModuleRoot(
  createFile: (filePath: string, content?: string, isDirectory?: boolean, contentType?: string) => Promise<boolean>,
  rootPath: string,
) {
  await createFile(PROGRAM_FILES_ROOT, '', true);
  await createFile(MODULES_ROOT, '', true);
  await createFile(rootPath, '', true);
  await ensureDir(PROGRAM_FILES_ROOT);
  await ensureDir(MODULES_ROOT);
  await ensureDir(rootPath);
}

export function launchInstalledMapleMarketModule(options: {
  appId?: InstalledRuntimeAppId;
  embedded?: boolean;
  productName: string;
  rootPath: string;
  sku?: string;
  navigate?: ReturnType<typeof useNavigate>;
}) {
  const title = options.productName;
  const appId = options.appId || 'web-app-runner';

  if (options.sku) {
    markPlayedMapleMarketProduct(options.sku);
  }

  if (options.embedded) {
    window.dispatchEvent(
      new CustomEvent('operator:openWindow', {
        detail: {
          appId,
          title,
          filePath: options.rootPath,
        },
      })
    );
    return;
  }

  const url = `/start?open=${appId}&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(options.rootPath)}`;
  if (options.navigate) {
    options.navigate(url);
    return;
  }

  window.location.href = url;
}

export function useMapleMarketInstaller() {
  const { createFile, getFile } = useUserVFS();
  const { user } = useAuth();
  const navigate = useNavigate();

  const installBundleInstall = useCallback(
    async ({
      product,
      signedUrl,
      version,
      onProgress,
    }: {
      product: MapleMarketApp;
      signedUrl: string;
      version: string;
      onProgress?: (stage: string) => void;
    }) => {
      onProgress?.('downloading_package');
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error('Failed to download package');
      }

      const html = await response.text();
      const rootPath = `${MODULES_ROOT}\\${sanitizeModuleName(product.name)}`;
      const manifest = JSON.stringify(createBundleManifest(product, version), null, 2);

      onProgress?.('installing_files');
      await ensureModuleRoot(createFile, rootPath);

      const manifestPath = `${rootPath}\\manifest.json`;
      const entryPath = `${rootPath}\\index.html`;
      await createFile(manifestPath, manifest, false, 'application/json');
      await ensureFile(manifestPath, manifest);
      await createFile(entryPath, html, false, 'text/html');
      await ensureFile(entryPath, html);

      return {
        appId: resolveRuntimeAppId('web') as InstalledRuntimeAppId,
        productName: product.name,
        resolvedVersion: version,
        rootPath,
      };
    },
    [createFile],
  );

  const installPackageInstall = useCallback(
    async ({
      product,
      signedUrl,
      version,
      onProgress,
    }: {
      product: MapleMarketApp;
      signedUrl: string;
      version: string;
      onProgress?: (stage: string) => void;
    }) => {
      onProgress?.('downloading_package');
      const response = await fetch(signedUrl);
      if (!response.ok) {
        throw new Error('Failed to download package');
      }

      const bytes = new Uint8Array(await response.arrayBuffer());
      const rootPath = `${MODULES_ROOT}\\${sanitizeModuleName(product.name)}`;

      onProgress?.('validating_manifest');
      const extracted = extractMapleMarketPackageArchive(bytes, rootPath);

      onProgress?.('installing_files');
      await ensureModuleRoot(createFile, rootPath);

      for (const file of extracted.files) {
        const lastSlash = file.fullPath.lastIndexOf('\\');
        if (lastSlash > 2) {
          const dirPath = file.fullPath.slice(0, lastSlash);
          await createFile(dirPath, '', true);
          await ensureDir(dirPath);
        }

        await createFile(file.fullPath, file.content, false, file.contentType);
        await ensureFile(file.fullPath, file.content);
      }

      return {
        appId: resolveRuntimeAppId(extracted.manifest.type),
        productName: extracted.manifest.name || product.name,
        resolvedVersion: extracted.manifest.version || version,
        rootPath,
      };
    },
    [createFile],
  );

  const installProduct = useCallback(
    async ({
      product,
      signedUrl,
      version,
      onProgress,
    }: {
      product: MapleMarketApp;
      signedUrl: string;
      version: string;
      onProgress?: (stage: string) => void;
    }) => {
      if (!user) {
        throw new Error('Authentication required');
      }

      const installResult = product.packageType === 'package'
        ? await installPackageInstall({ product, signedUrl, version, onProgress })
        : await installBundleInstall({ product, signedUrl, version, onProgress });

      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: installResult.rootPath } }));

      onProgress?.('recording_install');
      await (supabase.from('marketplace_installs') as any).insert({
        user_id: user.id,
        sku: product.sku,
        version: installResult.resolvedVersion,
        action: 'install',
        user_agent: navigator.userAgent,
        platform: navigator.platform,
      });

      saveInstalledMapleMarketProduct({
        sku: product.sku,
        rootPath: installResult.rootPath,
        version: installResult.resolvedVersion,
        productName: installResult.productName,
        installedAt: new Date().toISOString(),
        lastPlayedAt: null,
      });

      onProgress?.('launching_app');
      launchInstalledMapleMarketModule({
        appId: installResult.appId,
        embedded: true,
        productName: installResult.productName,
        rootPath: installResult.rootPath,
        sku: product.sku,
        navigate,
      });

      onProgress?.('ready');
      return { rootPath: installResult.rootPath, appId: installResult.appId };
    },
    [installBundleInstall, installPackageInstall, navigate, user],
  );

  const runInstalledModule = useCallback(
    (options: { productName: string; rootPath: string; embedded?: boolean; sku?: string }) => {
      const manifestContent = readInstalledManifestContent(options.rootPath, getFile);
      const appId = resolveInstalledRuntimeFromManifestContent(manifestContent);
      launchInstalledMapleMarketModule({ ...options, appId, navigate });
    },
    [getFile, navigate],
  );

  return {
    installProduct,
    installBundle: installProduct,
    launchInstalledMapleMarketModule: (options: { productName: string; rootPath: string; embedded?: boolean; sku?: string }) => {
      const manifestContent = readInstalledManifestContent(options.rootPath, getFile);
      const appId = resolveInstalledRuntimeFromManifestContent(manifestContent);
      launchInstalledMapleMarketModule({ ...options, appId, navigate });
    },
    runInstalledModule,
  };
}
